import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Save, X, AlertTriangle } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const EDITABLE_FIELDS = [
  { key: 'dialed', label: 'Dialed', type: 'number' },
  { key: 'dialed_company_ivr', label: 'Dialed Company IVR', type: 'number' },
  { key: 'dialed_fd_phone', label: 'Dialed FD Phone', type: 'number' },
  { key: 'connected', label: 'Connected', type: 'number' },
  { key: 'visit_scheduled', label: 'Visit Scheduled', type: 'number' },
  { key: 'visit_done', label: 'Visit Done', type: 'number' },
  { key: 'closure_expected', label: 'Closure Expected', type: 'number' },
  { key: 'revenue_expected', label: 'Revenue Expected', type: 'number' },
  { key: 'closure_phone', label: 'Closure Phone', type: 'number' },
  { key: 'closure_counter', label: 'Closure Counter', type: 'number' },
  { key: 'fd_availability', label: 'FD Availability', type: 'number' },
];

const EditPulseEntryDialog = ({ open, onOpenChange, entry, onSuccess }) => {
  const [formData, setFormData] = useState({});
  const [editReason, setEditReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data from entry when dialog opens
  useEffect(() => {
    if (entry && open) {
      const initial = {};
      EDITABLE_FIELDS.forEach(({ key }) => {
        initial[key] = entry[key] ?? '';
      });
      initial.comments = entry.comments ?? '';
      setFormData(initial);
      setEditReason('');
      setErrors({});
    }
  }, [entry, open]);

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    // Clear field error on change
    if (errors[key]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    // Validate edit reason
    const newErrors = {};
    if (!editReason || editReason.trim().length < 5) {
      newErrors.editReason = 'Reason must be at least 5 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build PATCH body with only changed fields
    const changedFields = {};
    EDITABLE_FIELDS.forEach(({ key, type }) => {
      const currentValue = type === 'number'
        ? (parseFloat(formData[key]) || 0)
        : formData[key];
      const originalValue = type === 'number'
        ? (parseFloat(entry[key]) || 0)
        : (entry[key] ?? '');

      if (currentValue !== originalValue) {
        changedFields[key] = currentValue;
      }
    });

    // Check comments separately
    const currentComments = formData.comments || '';
    const originalComments = entry.comments || '';
    if (currentComments !== originalComments) {
      changedFields.comments = currentComments;
    }

    if (Object.keys(changedFields).length === 0) {
      toast.error('No changes detected');
      return;
    }

    // Add edit reason
    changedFields.edit_reason = editReason.trim();

    setIsSubmitting(true);
    try {
      await axiosInstance.patch(`/pulse-entries/${entry.id}/edit`, changedFields);
      toast.success('Entry updated. All verifications have been reset.');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Failed to update entry';
      if (status === 400 || status === 403 || status === 404) {
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!entry) return null;

  const editCount = entry.edit_count || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit Pulse Entry
            {editCount > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                Edit {editCount + 1}/3
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {entry.gym_name} - {entry.entry_date} ({entry.pulse_name})
          </DialogDescription>
        </DialogHeader>

        {/* Read-only context */}
        <div className="bg-gray-50 border rounded-md p-3 text-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div><span className="text-gray-500">Date:</span> <span className="font-medium">{entry.entry_date}</span></div>
            <div><span className="text-gray-500">Gym:</span> <span className="font-medium">{entry.gym_name}</span></div>
            <div><span className="text-gray-500">Pulse:</span> <span className="font-medium">{entry.pulse_name}</span></div>
            <div><span className="text-gray-500">Source:</span> <span className="font-medium">{entry.source_name}</span></div>
          </div>
        </div>

        {/* Editable fields - 2 column grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {EDITABLE_FIELDS.map(({ key, label, type }) => (
            <div key={key}>
              <Label className="text-xs mb-1">{label}</Label>
              <Input
                type={type}
                min="0"
                step={key === 'revenue_expected' ? '1000' : '1'}
                className="h-9"
                value={formData[key] ?? ''}
                onChange={(e) => handleFieldChange(key, e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* Comments */}
        <div>
          <Label className="text-xs mb-1">Comments</Label>
          <Textarea
            className="h-16 text-sm"
            placeholder="Any additional notes..."
            value={formData.comments ?? ''}
            onChange={(e) => handleFieldChange('comments', e.target.value)}
          />
        </div>

        {/* Edit reason - mandatory */}
        <div>
          <Label className="text-xs mb-1 flex items-center gap-1">
            <AlertTriangle size={12} className="text-amber-500" />
            Reason for editing <span className="text-red-500">*</span>
          </Label>
          <Textarea
            className={`h-16 text-sm ${errors.editReason ? 'border-red-500' : ''}`}
            placeholder="Explain what you're correcting..."
            value={editReason}
            onChange={(e) => {
              setEditReason(e.target.value);
              if (errors.editReason) {
                setErrors(prev => {
                  const next = { ...prev };
                  delete next.editReason;
                  return next;
                });
              }
            }}
          />
          {errors.editReason && (
            <p className="text-red-500 text-xs mt-1">{errors.editReason}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            {editReason.trim().length}/5 min characters
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            <X size={14} className="mr-1" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-[#1F4E79] hover:bg-[#163a5c]"
          >
            <Save size={14} className="mr-1" />
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPulseEntryDialog;
